/**
 * Visualizer Manager — per-panel hook for visualizer mode switching & config.
 *
 * Returns mode state, available modes, manifest metadata, and mode-switch
 * actions. Controllers use the returned data to render PanelVisualizer
 * and wire quick-switch props.
 */

import { useMemo } from 'react';

import { useVisualizerModeSwitch } from '@/hooks/useAddVisualizerStep';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import { getManifestEntry, isEditable as isManifestEditable } from '@/lib/visualizerManifest';
import { VisualizerModeRegistry } from '@/lib/VisualizerModeRegistry';
import { type PanelRole,useVisualizerStore, type VisualizerMode } from '@/store/useVisualizerStore';

export function useVisualizerPanel(panelRole: PanelRole, semanticType: ISemanticType | null) {
  const vizMode = useVisualizerModeSwitch(panelRole);

  const storeMode = useVisualizerStore(
    (s) => (panelRole === 'input' ? s.inputPanel : s.outputPanel).mode,
  );
  const activeModes = useVisualizerStore(
    (s) => (panelRole === 'input' ? s.inputActiveModes : s.outputActiveModes),
  );
  const addActiveMode = useVisualizerStore((s) => s.addActiveMode);

  const availableModes = useMemo(() => {
    const allModes = VisualizerModeRegistry.getAvailableModes(semanticType);
    return allModes
      .filter((m) => !m.panes || m.panes.includes(panelRole))
      .map((m) => m.id) as VisualizerMode[];
  }, [semanticType, panelRole]);

  const effectiveModeName = getManifestEntry(storeMode)?.label ?? 'Raw';
  const isEditable = isManifestEditable(storeMode);

  return {
    storeMode,
    activeModes,
    availableModes,
    addActiveMode,
    effectiveModeName,
    isEditable,
    ...vizMode,
  };
}
